/**
 * A short-lived, same-browser handoff for assistant-produced drafts.
 *
 * The URL carries only an opaque key; draft context stays in sessionStorage so
 * it is not shared through links, analytics, server logs, or browser history.
 */
export const ASSISTANT_ARTIFACT_HANDOFF_VERSION = 1 as const;
export const ASSISTANT_ARTIFACT_HANDOFF_TTL_MS = 30 * 60 * 1000;
const PREFIX = 'scarlet-assistant-artifact-handoff:';

export const ASSISTANT_ARTIFACT_DESTINATIONS = {
  scheduling: '/scheduling',
  degree_planning: '/degree-planning',
  explore: '/explore',
  maps: '/classes-on-map',
  course_sniper: '/course-sniper',
} as const;

export type AssistantArtifactDestination = keyof typeof ASSISTANT_ARTIFACT_DESTINATIONS;

export interface AssistantArtifactHandoff {
  destination: AssistantArtifactDestination;
  href: string;
  draft_context: Record<string, unknown>;
}

export interface StoredAssistantArtifactHandoff {
  version: typeof ASSISTANT_ARTIFACT_HANDOFF_VERSION;
  destination: AssistantArtifactDestination;
  draftContext: Record<string, unknown>;
  expiresAt: number;
}

type StorageLike = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

type HandoffDraft = Record<string, unknown>;

function isDestination(value: unknown): value is AssistantArtifactDestination {
  return typeof value === 'string' && value in ASSISTANT_ARTIFACT_DESTINATIONS;
}

function stringList(value: unknown, pattern: RegExp) {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && pattern.test(entry)).slice(0, 12)
    : [];
}

/**
 * A handoff is a review-only bridge, not a generic cross-route data channel.
 * Each destination accepts exactly the small set of facts it can show without
 * creating data, submitting a request, or changing an existing saved record.
 */
export function safeDraftContext(destination: AssistantArtifactDestination, value: unknown): HandoffDraft {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const draft = value as Record<string, unknown>;
  switch (destination) {
    case 'scheduling':
      return { class_ids: stringList(draft.class_ids, /^\d{2}:\d{3}:\d{3}:\d{5}$/) };
    case 'degree_planning':
      return typeof draft.degree_id === 'number' && Number.isSafeInteger(draft.degree_id) && draft.degree_id > 0
        ? { degree_id: draft.degree_id }
        : {};
    case 'explore':
      return typeof draft.course_id === 'string' && /^\d{8}$/.test(draft.course_id)
        ? { course_id: draft.course_id }
        : {};
    case 'maps':
      return typeof draft.building_code === 'string' && /^[A-Z0-9]{2,8}$/.test(draft.building_code)
        ? { building_code: draft.building_code }
        : {};
    case 'course_sniper':
      return typeof draft.section_id === 'string' && /^\d{2}:\d{3}:\d{3}:\d{5}$/.test(draft.section_id)
        ? { section_id: draft.section_id }
        : {};
  }
}

export function createAssistantArtifactHandoff(
  handoff: AssistantArtifactHandoff,
  options?: { storage?: StorageLike; createId?: () => string; now?: number },
): string | null {
  if (!isDestination(handoff.destination)) return null;
  const storage = options?.storage ?? (typeof window === 'undefined' ? null : window.sessionStorage);
  if (!storage) return null;
  const id = (options?.createId ?? crypto.randomUUID)();
  const now = options?.now ?? Date.now();
  const baseHref = ASSISTANT_ARTIFACT_DESTINATIONS[handoff.destination];
  const href = handoff.href === baseHref ? handoff.href : baseHref;
  storage.setItem(`${PREFIX}${id}`, JSON.stringify({
    version: ASSISTANT_ARTIFACT_HANDOFF_VERSION,
    destination: handoff.destination,
    draftContext: safeDraftContext(handoff.destination, handoff.draft_context),
    expiresAt: now + ASSISTANT_ARTIFACT_HANDOFF_TTL_MS,
  } satisfies StoredAssistantArtifactHandoff));
  return `${href}?assistantArtifact=${encodeURIComponent(id)}`;
}

export function consumeAssistantArtifactHandoff(
  destination: AssistantArtifactDestination,
  handoffId: string,
  options?: { storage?: StorageLike; now?: number },
): Record<string, unknown> | null {
  const storage = options?.storage ?? (typeof window === 'undefined' ? null : window.sessionStorage);
  if (!storage || !handoffId) return null;
  const key = `${PREFIX}${handoffId}`;
  const raw = storage.getItem(key);
  storage.removeItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredAssistantArtifactHandoff>;
    if (
      parsed.version !== ASSISTANT_ARTIFACT_HANDOFF_VERSION
      || parsed.destination !== destination
      || typeof parsed.expiresAt !== 'number'
      || parsed.expiresAt <= (options?.now ?? Date.now())
    ) return null;
    return safeDraftContext(destination, parsed.draftContext);
  } catch {
    return null;
  }
}
